import type {TraceData} from '../../../utils/copilot-chat-reducer'
import type {SpanNode} from '../../../utils/tracing'

export interface TraceState {
  traceInfo?: {
    visible: boolean
    traces: ReadonlyMap<string, TraceData> // messageId to trace data
    loadedThreadIds: ReadonlySet<string>
  }
}

export type TraceAction =
  | {
      type: 'SET_TRACE_INFO'
      traceInfo: {
        visible?: boolean
        spanNode?: SpanNode
        messageId?: string
        threadId?: string
        actualTraceId?: string
        traces?: Map<string, TraceData>
      }
    }
  | {type: 'REMOVE_TRACE'; traceID: string} // traceID is the actualTraceId, not messageId
  | {type: 'REMOVE_TRACES_FOR_THREAD'; threadID: string}

const emptyTraces: ReadonlyMap<string, TraceData> = new Map<string, TraceData>()
const emptyLoadedThreadIds: ReadonlySet<string> = new Set<string>()

export function traceReducer(state: TraceState, action: TraceAction): TraceState {
  switch (action.type) {
    case 'SET_TRACE_INFO': {
      const {visible, spanNode, messageId, threadId, actualTraceId, traces} = action.traceInfo

      let updatedTraces = state.traceInfo?.traces || emptyTraces
      let updatedLoadedThreadIds = state.traceInfo?.loadedThreadIds || emptyLoadedThreadIds

      if (traces && traces.size > 0) {
        // Merge new traces with existing ones
        const newTraces = new Map(updatedTraces)
        for (const [msgId, traceData] of traces) {
          newTraces.set(msgId, traceData)
        }
        updatedTraces = newTraces
      } else if (spanNode && messageId && threadId) {
        // Handle single trace update (from trace save)
        const newTraces = new Map(updatedTraces)
        const timestamp = new Date(spanNode.startTime).toISOString()
        newTraces.set(messageId, {
          spanNode,
          threadId,
          actualTraceId: actualTraceId || '',
          timestamp,
        })
        updatedTraces = newTraces
      }

      if (threadId && !updatedLoadedThreadIds.has(threadId)) {
        const newLoadedThreadIds = new Set(updatedLoadedThreadIds)
        newLoadedThreadIds.add(threadId)
        updatedLoadedThreadIds = newLoadedThreadIds
      }

      const updatedVisible = visible !== undefined ? visible : state.traceInfo?.visible || false

      return {
        ...state,
        traceInfo: {
          visible: updatedVisible,
          traces: updatedTraces,
          loadedThreadIds: updatedLoadedThreadIds,
        },
      }
    }
    case 'REMOVE_TRACE': {
      const currentTraces = new Map(state.traceInfo?.traces || [])

      // Find and remove trace by actualTraceId
      for (const [messageId, traceData] of currentTraces) {
        if (traceData.actualTraceId === action.traceID) {
          currentTraces.delete(messageId)
          break
        }
      }

      return {
        ...state,
        traceInfo: {
          visible: state.traceInfo?.visible || false,
          traces: currentTraces,
          loadedThreadIds: state.traceInfo?.loadedThreadIds || new Set(),
        },
      }
    }
    case 'REMOVE_TRACES_FOR_THREAD': {
      const currentTraces = new Map(state.traceInfo?.traces || [])

      // Delete all traces for thread
      for (const [msgId, traceData] of currentTraces) {
        if (traceData.threadId === action.threadID) {
          currentTraces.delete(msgId)
        }
      }

      return {
        ...state,
        traceInfo: {
          visible: state.traceInfo?.visible || false,
          traces: currentTraces,
          loadedThreadIds: state.traceInfo?.loadedThreadIds || new Set(),
        },
      }
    }
  }
}
